import {
  EASE_PREMIUM_OUT,
  MOBILE_COAST_MIN_DURATION_SEC,
  MOBILE_COAST_SPEED_FACTOR,
  MOBILE_RELEASE_DISTANCE_MULTIPLIER,
  MOBILE_RELEASE_MAX_COAST_VH,
  MOBILE_SMOOTH_DURATION_SEC,
} from "@/scrollPresets";
import { getActiveMobileScrollGate } from "@/mobileScrollGate";
import { afterTouchRelease } from "@/mobileSafariScroll";
import {
  shouldBlockMobileTouchCoast,
  shouldSkipMobileTouchCoast,
} from "@/mobileSectionSnap";

const MOBILE_RELEASE_MIN_SPEED_PX_S = 4;

/**
 * Mobile only: short ease-out glide after thumb lift.
 * @param {import('lenis').default} lenis
 * @returns {() => void}
 */
export function attachMobileTouchReleaseCoast(lenis) {
  const samples = [];
  let tracking = false;

  const onTouchStart = () => {
    if (lenis.isStopped) lenis.start();
    tracking = true;
    samples.length = 0;
  };

  const onTouchMove = (e) => {
    if (!tracking) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    samples.push({ t: performance.now(), y: touch.clientY });
    if (samples.length > 14) samples.shift();
  };

  const runCoast = () => {
    if (shouldBlockMobileTouchCoast()) return;
    if (MOBILE_RELEASE_MAX_COAST_VH <= 0 || samples.length < 2) return;

    const first = samples[0];
    const last = samples[samples.length - 1];
    const dtSec = (last.t - first.t) / 1000;
    if (dtSec <= 0.005) return;

    const vy = (first.y - last.y) / dtSec;
    if (Math.abs(vy) < MOBILE_RELEASE_MIN_SPEED_PX_S) return;
    if (shouldSkipMobileTouchCoast(lenis, vy)) return;

    const vh = window.visualViewport?.height ?? window.innerHeight ?? 800;
    const maxCoast = vh * MOBILE_RELEASE_MAX_COAST_VH;
    const coastPx = Math.max(
      -maxCoast,
      Math.min(
        maxCoast,
        vy *
          MOBILE_SMOOTH_DURATION_SEC *
          MOBILE_RELEASE_DISTANCE_MULTIPLIER *
          MOBILE_COAST_SPEED_FACTOR,
      ),
    );

    if (lenis.isStopped) lenis.start();

    const limit = lenis.limit ?? 0;
    let target = Math.max(0, Math.min(limit, lenis.scroll + coastPx));
    const bounds = getActiveMobileScrollGate();
    if (bounds) {
      const ceiling = Math.min(
        bounds.maxScrollY,
        bounds.sectionEnd ?? bounds.maxScrollY,
      );
      target = Math.max(bounds.minScrollY, Math.min(ceiling, target));
    }
    const travelPx = Math.abs(target - lenis.scroll);
    if (travelPx < 1) return;

    const coastSpeed = Math.max(
      Math.abs(vy) * MOBILE_COAST_SPEED_FACTOR,
      MOBILE_RELEASE_MIN_SPEED_PX_S * MOBILE_COAST_SPEED_FACTOR,
    );
    const duration = Math.min(
      MOBILE_SMOOTH_DURATION_SEC,
      Math.max(MOBILE_COAST_MIN_DURATION_SEC, travelPx / coastSpeed),
    );

    lenis.scrollTo(target, {
      duration,
      easing: EASE_PREMIUM_OUT,
      programmatic: true,
      force: true,
    });
  };

  const onTouchEnd = () => {
    tracking = false;
    afterTouchRelease(runCoast);
  };

  const touchOpts = { passive: true, capture: true };
  document.addEventListener("touchstart", onTouchStart, touchOpts);
  document.addEventListener("touchmove", onTouchMove, touchOpts);
  document.addEventListener("touchend", onTouchEnd, touchOpts);
  document.addEventListener("touchcancel", onTouchEnd, touchOpts);

  return () => {
    document.removeEventListener("touchstart", onTouchStart, touchOpts);
    document.removeEventListener("touchmove", onTouchMove, touchOpts);
    document.removeEventListener("touchend", onTouchEnd, touchOpts);
    document.removeEventListener("touchcancel", onTouchEnd, touchOpts);
  };
}
